(function initializeProductPageRedesign() {
  const originalProductGalleryMarkup = new WeakMap();
  const productGalleryPreloadTasks = new WeakMap();

  function rememberProductGalleryMarkup(gallery) {
    if (!gallery || originalProductGalleryMarkup.has(gallery)) return;

    originalProductGalleryMarkup.set(gallery, gallery.innerHTML);
  }

  function preloadProductGalleryImages(gallery) {
    if (!gallery || productGalleryPreloadTasks.has(gallery)) return;

    const galleryImages = Array.from(gallery.querySelectorAll('.productView-image'));
    const variants = getVariantData(gallery.closest('.productView'));
    const featuredMediaIds = new Set(
      variants
        .map((variant) => normalizeMediaId(variant.featured_media?.id))
        .filter(Boolean)
    );
    const featuredImageSources = galleryImages
      .filter((galleryImage) => {
        const mediaContainer = galleryImage.querySelector('[data-media-id]');
        return featuredMediaIds.has(normalizeMediaId(mediaContainer?.dataset.mediaId));
      })
      .map((galleryImage) => {
        const image = galleryImage.querySelector('img');
        return image?.dataset.src || image?.currentSrc || image?.src;
      })
      .filter(Boolean);
    const remainingImageSources = galleryImages
      .map((galleryImage) => {
        const image = galleryImage.querySelector('img');
        return image?.dataset.src || image?.currentSrc || image?.src;
      })
      .filter(Boolean);
    const imageSources = [...new Set([...featuredImageSources, ...remainingImageSources])];
    const highPrioritySources = new Set(featuredImageSources);
    const preloadTask = new Promise((resolve) => {
      const preloadQueue = [...imageSources];
      const concurrentPreloads = Math.min(4, preloadQueue.length);
      let completedPreloads = 0;

      if (!concurrentPreloads) {
        resolve();
        return;
      }

      const preloadNextImage = () => {
        const imageSource = preloadQueue.shift();

        if (!imageSource) {
          completedPreloads += 1;
          if (completedPreloads === concurrentPreloads) resolve();
          return;
        }

        const preloadImage = new Image();
        preloadImage.decoding = 'async';
        preloadImage.fetchPriority = highPrioritySources.has(imageSource) ? 'high' : 'low';
        preloadImage.onload = preloadNextImage;
        preloadImage.onerror = preloadNextImage;
        preloadImage.src = imageSource;
      };

      for (let preloadIndex = 0; preloadIndex < concurrentPreloads; preloadIndex += 1) {
        preloadNextImage();
      }
    });

    productGalleryPreloadTasks.set(gallery, preloadTask);
  }

  function getSelectedColorFilterNames(input) {
    const filterNames = [];
    const primaryFilter = input.dataset.filter?.replace(/^\./, '');
    const metafieldFilters = input.dataset.metafieldsVig
      ?.split(',')
      .map((value) => `filter-${value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-')}`)
      .filter(Boolean) || [];

    if (primaryFilter) filterNames.push(primaryFilter);
    filterNames.push(...metafieldFilters);

    return [...new Set(filterNames)];
  }

  function normalizeMediaId(mediaId) {
    return String(mediaId || '').split('-').pop();
  }

  function getVariantData(productView) {
    const variantDataScript = productView?.querySelector(
      'variant-radios script[type="application/json"], variant-selects script[type="application/json"]'
    );

    if (!variantDataScript) return [];

    try {
      return JSON.parse(variantDataScript.textContent);
    } catch (error) {
      console.warn('Unable to parse product variant media data.', error);
      return [];
    }
  }

  function getVariantMediaGroup(input, gallery) {
    const productView = input.closest('.productView');
    const variants = getVariantData(productView);
    const selectedVariantId = String(input.dataset.variantId || '');
    const selectedVariant = variants.find((variant) => String(variant.id) === selectedVariantId);
    const galleryImages = Array.from(gallery.querySelectorAll('.productView-image')).filter(
      (galleryImage) => !galleryImage.classList.contains('slick-cloned') &&
        !galleryImage.closest('.slick-cloned')
    );
    const galleryMediaIds = galleryImages.map((galleryImage) => {
      const mediaContainer = galleryImage.querySelector('[data-media-id]');
      return normalizeMediaId(mediaContainer?.dataset.mediaId);
    });
    const selectedFeaturedMediaId = normalizeMediaId(selectedVariant?.featured_media?.id);
    const selectedStartIndex = galleryMediaIds.indexOf(selectedFeaturedMediaId);

    if (selectedStartIndex < 0) return [];

    const groupStartIndexes = variants
      .map((variant) => {
        const featuredMediaId = normalizeMediaId(variant.featured_media?.id);
        return galleryMediaIds.indexOf(featuredMediaId);
      })
      .filter((index) => index >= 0)
      .filter((index, position, indexes) => indexes.indexOf(index) === position)
      .sort((firstIndex, secondIndex) => firstIndex - secondIndex);
    const nextStartIndex = groupStartIndexes.find((index) => index > selectedStartIndex);
    const selectedEndIndex = nextStartIndex === undefined
      ? galleryMediaIds.length
      : nextStartIndex;

    return galleryMediaIds.slice(selectedStartIndex, selectedEndIndex).filter(Boolean);
  }

  function galleryImageMatchesFilters(galleryImage, filterNames) {
    return filterNames.some((filterName) => {
      return Array.from(galleryImage.classList).some((className) => {
        return className === filterName || className.startsWith(`${filterName}-`);
      });
    });
  }

  function loadRebuiltGalleryImages(gallery) {
    gallery.querySelectorAll('img').forEach((image, imageIndex) => {
      if (image.dataset.src && !image.getAttribute('src')) {
        image.setAttribute('src', image.dataset.src);
      }

      if (image.dataset.srcset && !image.getAttribute('srcset')) {
        image.setAttribute('srcset', image.dataset.srcset);
      }

      image.loading = 'eager';
      image.fetchPriority = imageIndex === 0 ? 'high' : 'auto';
      image.classList.remove('lazyload', 'lazyloading');
      image.classList.add('lazyloaded');
    });
  }

  function updateMobileGalleryViewportHeight(gallery) {
    if (!gallery) return;

    const firstGalleryImage = gallery.querySelector(':scope > .productView-image');
    const firstProductImage = firstGalleryImage?.querySelector('img');

    if (!firstGalleryImage || !firstProductImage) return;

    const applyMeasuredHeight = () => {
      const measuredHeight = firstGalleryImage.getBoundingClientRect().height;
      if (measuredHeight <= 0) return;

      const viewportHeight = `${Math.round(measuredHeight)}px`;
      const productTop = gallery.closest('.productView-top');

      gallery.style.setProperty('--product-gallery-viewport-height', viewportHeight);
      productTop?.style.setProperty('--product-gallery-viewport-height', viewportHeight);
    };

    if (firstProductImage.complete && firstProductImage.naturalWidth > 0) {
      window.requestAnimationFrame(applyMeasuredHeight);
      return;
    }

    firstProductImage.addEventListener('load', applyMeasuredHeight, { once: true });
  }

  function rebuildMobileGalleryForColor(input) {
    const productView = input.closest('.productView');
    const gallery = productView?.querySelector('[data-image-gallery-main]');
    const filterNames = getSelectedColorFilterNames(input);
    const originalMarkup = gallery && originalProductGalleryMarkup.get(gallery);
    const filterKey = filterNames.join('|');

    if (!gallery || !originalMarkup || !filterNames.length) return;

    if (window.jQuery && gallery.classList.contains('slick-initialized')) {
      window.jQuery(gallery).slick('unslick');
    }

    gallery.innerHTML = originalMarkup;
    gallery.classList.add(
      'media-filter',
      'productView-nav--mobileVerticalList',
      'productView-nav--unifiedVerticalList'
    );
    gallery.classList.remove('slick-filter', 'slick-initialized');
    gallery.dataset.activeMobileColorFilter = filterKey;

    const galleryImages = Array.from(gallery.querySelectorAll(':scope > .productView-image'));
    const variantMediaGroup = getVariantMediaGroup(input, gallery);
    const matchingImages = variantMediaGroup.length
      ? galleryImages.filter((galleryImage) => {
        const mediaContainer = galleryImage.querySelector('[data-media-id]');
        return variantMediaGroup.includes(normalizeMediaId(mediaContainer?.dataset.mediaId));
      })
      : galleryImages.filter((galleryImage) => {
        return galleryImageMatchesFilters(galleryImage, filterNames);
      });

    if (matchingImages.length) {
      galleryImages.forEach((galleryImage) => {
        if (!matchingImages.includes(galleryImage)) galleryImage.remove();
      });
    }

    gallery.querySelectorAll(':scope > .productView-image').forEach((galleryImage, index) => {
      const displayIndex = String(index + 1);
      galleryImage.dataset.index = displayIndex;
      galleryImage.querySelectorAll('[data-index]').forEach((indexedElement) => {
        indexedElement.dataset.index = displayIndex;
      });
    });

    gallery.scrollTop = 0;
    loadRebuiltGalleryImages(gallery);
    updateMobileGalleryViewportHeight(gallery);
    initializeMobileGalleryPagination(gallery);
    window.requestAnimationFrame(() => {
      gallery.scrollTop = 0;
      updateMobileGalleryPagination(gallery);
    });
    document.dispatchEvent(new CustomEvent('product:mobile-gallery-updated'));
  }

  function isElementVisible(element) {
    if (!element) return false;

    const elementStyle = window.getComputedStyle(element);
    const elementRectangle = element.getBoundingClientRect();

    return elementStyle.display !== 'none' &&
      elementStyle.visibility !== 'hidden' &&
      elementRectangle.width > 0 &&
      elementRectangle.height > 0;
  }

  function getVisibleProductLightboxTriggers(activeTrigger) {
    const gallery = activeTrigger.closest('[data-image-gallery-main]');
    const selector = '[data-product-fancybox="images"]';

    if (!gallery) {
      return Array.from(document.querySelectorAll(selector)).filter(isElementVisible);
    }

    const visibleTriggers = Array.from(gallery.querySelectorAll(selector)).filter((trigger) => {
      const slide = trigger.closest('.productView-image, .slick-slide');

      return !trigger.closest('.slick-cloned') &&
        (!slide || isElementVisible(slide)) &&
        isElementVisible(trigger);
    });

    return visibleTriggers.length ? visibleTriggers : [activeTrigger];
  }

  function renderLightboxThumbs(instance, activeIndex = 0) {
    const container = instance?.$refs?.container?.[0];
    if (!container) return;

    let rail = container.querySelector('[data-product-lightbox-thumbs]');

    if (!rail) {
      rail = document.createElement('div');
      rail.className = 'product-media-lightbox__thumbs';
      rail.setAttribute('data-product-lightbox-thumbs', '');
      rail.style.setProperty(
        '--product-lightbox-visible-thumbs',
        String(Math.min(instance.group.length, 5))
      );

      instance.group.forEach((item, index) => {
        const button = document.createElement('button');
        const image = document.createElement('img');
        const thumbnail = item.opts?.thumb || item.src;

        button.type = 'button';
        button.className = 'product-media-lightbox__thumb';
        button.dataset.lightboxIndex = String(index);
        button.setAttribute('aria-label', `View image ${index + 1}`);
        button.addEventListener('click', () => {
          if (instance.current?.index === index) return;

          // Direct jumps can animate every intermediate slide in Fancybox v3.
          // Use an instant jump so distant thumbnails behave like adjacent ones.
          instance.jumpTo(index, 0);
        });

        image.src = thumbnail;
        image.alt = '';
        image.loading = 'lazy';
        button.appendChild(image);
        rail.appendChild(button);
      });

      container.appendChild(rail);
    }

    rail.querySelectorAll('[data-lightbox-index]').forEach((button) => {
      const isActive = Number(button.dataset.lightboxIndex) === activeIndex;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-current', isActive ? 'true' : 'false');

      if (isActive) {
        if (window.innerWidth <= 767) {
          const targetLeft = button.offsetLeft -
            (rail.clientWidth - button.offsetWidth) / 2;
          rail.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
        } else {
          button.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      }
    });
  }

  const lightboxOptions = {
    baseClass: 'product-media-lightbox',
    animationEffect: false,
    animationDuration: 0,
    buttons: ['close'],
    arrows: false,
    infobar: false,
    loop: true,
    transitionEffect: false,
    transitionDuration: 0,
    thumbs: {
      autoStart: false
    },
    afterShow(instance, current) {
      renderLightboxThumbs(instance, current.index);
    }
  };

  function bindThumbnailWheel(slider) {
    if (slider.dataset.wheelNavigationBound === 'true') return;

    slider.dataset.wheelNavigationBound = 'true';
    slider.addEventListener('wheel', (event) => {
      if (window.innerWidth < 1025 || !slider.classList.contains('slick-initialized')) return;

      event.preventDefault();
      window.jQuery(slider).slick(event.deltaY > 0 ? 'slickNext' : 'slickPrev');
    }, { passive: false });
  }

  function arrangeProductDetails() {
    const details = document.querySelector('.template-product .product-default .productView-product');
    const tabs = details?.querySelector('.productView-tab');
    const benefits = details?.querySelector('.productView-benefits');
    const worryFree = details?.querySelector('[id*="worry_free_purchase"]');

    if (!tabs || !benefits || !worryFree) return;

    tabs.insertAdjacentElement('afterend', benefits);
    benefits.insertAdjacentElement('afterend', worryFree);
    styleWorryFreePurchase(worryFree);
  }

  function styleWorryFreePurchase(worryFree) {
    const referenceText = worryFree
      .closest('.productView-product')
      ?.querySelector('.productView-tab .toggleLink .text');
    const referenceFontFamily = referenceText
      ? window.getComputedStyle(referenceText).fontFamily
      : window.getComputedStyle(worryFree).fontFamily;
    const referenceFontSize = referenceText
      ? window.getComputedStyle(referenceText).fontSize
      : window.getComputedStyle(worryFree).fontSize;
    const referenceFontWeight = referenceText
      ? window.getComputedStyle(referenceText).fontWeight
      : window.getComputedStyle(worryFree).fontWeight;
    worryFree.style.setProperty('font-family', referenceFontFamily, 'important');
    worryFree.style.setProperty('font-size', referenceFontSize, 'important');
    worryFree.style.setProperty('font-weight', referenceFontWeight, 'important');

    const textSelector = 'div, a, button, p, span:not([class*="icon"]):not([class*="logo"]), strong';
    const styleContent = `
      :host, ${textSelector} {
        font-family: ${referenceFontFamily} !important;
        font-size: ${referenceFontSize} !important;
        font-weight: ${referenceFontWeight} !important;
      }
      .seal-pdp-widget {
        align-items: center !important;
      }
      .seal-pdp-widget-outer {
        transform: translateX(-27px) !important;
      }
      svg, img, [class*="icon"], [class*="logo"] {
        width: 16px !important;
        height: 16px !important;
        min-width: 16px !important;
        min-height: 16px !important;
        max-width: none !important;
        max-height: none !important;
        flex: 0 0 16px !important;
      }
    `;
    const observedRoots = new WeakSet();

    const applyShadowStyles = (root) => {
      root.querySelectorAll('.seal-pdp-widget').forEach((widget) => {
        widget.style.setProperty('align-items', 'center', 'important');
      });

      root.querySelectorAll('.seal-pdp-widget-outer').forEach((widgetOuter) => {
        widgetOuter.style.setProperty('transform', 'translateX(-27px)', 'important');
      });

      root.querySelectorAll(textSelector).forEach((textElement) => {
        textElement.style.setProperty('font-family', referenceFontFamily, 'important');
        textElement.style.setProperty('font-size', referenceFontSize, 'important');
        textElement.style.setProperty('font-weight', referenceFontWeight, 'important');
      });

      root.querySelectorAll('svg, img, [class*="icon"], [class*="logo"]').forEach((icon) => {
        icon.style.setProperty('width', '16px', 'important');
        icon.style.setProperty('height', '16px', 'important');
        icon.style.setProperty('min-width', '16px', 'important');
        icon.style.setProperty('min-height', '16px', 'important');
        icon.style.setProperty('max-width', 'none', 'important');
        icon.style.setProperty('max-height', 'none', 'important');
        icon.style.setProperty('flex', '0 0 16px', 'important');
      });

      [root, ...root.querySelectorAll('*')].forEach((element) => {
        if (!element.shadowRoot) return;

        if (!element.shadowRoot.querySelector('[data-worry-free-product-styles]')) {
          const style = document.createElement('style');
          style.dataset.worryFreeProductStyles = 'true';
          style.textContent = styleContent;
          element.shadowRoot.appendChild(style);
        }

        applyShadowStyles(element.shadowRoot);

        if (!observedRoots.has(element.shadowRoot)) {
          observedRoots.add(element.shadowRoot);
          const shadowObserver = new MutationObserver(() => {
            applyShadowStyles(element.shadowRoot);
          });
          shadowObserver.observe(element.shadowRoot, { childList: true, subtree: true });
        }
      });
    };

    applyShadowStyles(worryFree);

    const observer = new MutationObserver(() => applyShadowStyles(worryFree));
    observer.observe(worryFree, { childList: true, subtree: true });
  }

  function updateMobileGalleryPagination(slider) {
    const pagination = slider
      .closest('.productView-image-wrapper')
      ?.querySelector('[data-mobile-product-pagination]');

    if (!pagination) return;

    const galleryImages = Array.from(
      slider.querySelectorAll(':scope > .productView-image')
    );
    const viewportCenter = slider.scrollTop + slider.clientHeight / 2;
    let activeImageIndex = 0;
    let nearestImageDistance = Number.POSITIVE_INFINITY;

    galleryImages.forEach((galleryImage, imageIndex) => {
      const imageCenter = galleryImage.offsetTop + galleryImage.offsetHeight / 2;
      const imageDistance = Math.abs(imageCenter - viewportCenter);

      if (imageDistance < nearestImageDistance) {
        nearestImageDistance = imageDistance;
        activeImageIndex = imageIndex;
      }
    });

    pagination.querySelectorAll('[data-mobile-product-pagination-index]')
      .forEach((paginationButton, buttonIndex) => {
        const isActive = buttonIndex === activeImageIndex;
        paginationButton.classList.toggle('is-active', isActive);
        paginationButton.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
  }

  function initializeMobileGalleryPagination(slider) {
    const imageWrapper = slider.closest('.productView-image-wrapper');
    const galleryImages = Array.from(
      slider.querySelectorAll(':scope > .productView-image')
    );

    if (!imageWrapper || !galleryImages.length) return;

    const existingPagination = imageWrapper.querySelector(
      '[data-mobile-product-pagination]'
    );
    const existingPaginationButtons = existingPagination?.querySelectorAll(
      '[data-mobile-product-pagination-index]'
    );

    if (existingPaginationButtons?.length === galleryImages.length) {
      updateMobileGalleryPagination(slider);
      return;
    }

    existingPagination?.remove();

    const pagination = document.createElement('div');
    pagination.className = 'productView-mobilePagination';
    pagination.setAttribute('data-mobile-product-pagination', '');
    pagination.setAttribute('aria-label', 'Product image navigation');

    galleryImages.forEach((galleryImage, imageIndex) => {
      const paginationButton = document.createElement('button');

      paginationButton.type = 'button';
      paginationButton.className = 'productView-mobilePaginationDot';
      paginationButton.dataset.mobileProductPaginationIndex = String(imageIndex);
      paginationButton.setAttribute('aria-label', `View product image ${imageIndex + 1}`);
      paginationButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const currentGalleryImages = Array.from(
          slider.querySelectorAll(':scope > .productView-image')
        );
        const targetGalleryImage = currentGalleryImages[imageIndex] ||
          (galleryImage.isConnected ? galleryImage : null);

        if (!targetGalleryImage) return;

        const sliderBounds = slider.getBoundingClientRect();
        const targetImageBounds = targetGalleryImage.getBoundingClientRect();
        const targetScrollTop = slider.scrollTop +
          targetImageBounds.top -
          sliderBounds.top;

        slider.scrollTo({
          top: Math.max(0, targetScrollTop),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth'
        });
      });

      pagination.appendChild(paginationButton);
    });

    imageWrapper.appendChild(pagination);
    updateMobileGalleryPagination(slider);
  }

  function bindMobileVerticalGallery(slider) {
    if (slider.dataset.mobileVerticalGalleryBound === 'true') return;

    slider.dataset.mobileVerticalGalleryBound = 'true';
    let startY = 0;
    let previousTouchY = 0;
    let tracking = false;
    let isHandingOffToProductSheet = false;
    let paginationFrame = null;
    let pointerStartY = 0;
    let pointerStartScrollTop = 0;
    let activePointerId = null;
    let pointerDidDrag = false;

    const dispatchProductSheetDrag = (phase, clientY) => {
      document.dispatchEvent(new CustomEvent(`product:sheet-drag-${phase}`, {
        detail: { clientY }
      }));
    };

    slider.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) return;

      startY = event.touches[0].clientY;
      previousTouchY = startY;
      tracking = true;
      isHandingOffToProductSheet = false;
    }, { capture: true, passive: true });

    slider.addEventListener('touchmove', (event) => {
      if (!tracking || event.touches.length !== 1) return;

      const currentTouchY = event.touches[0].clientY;

      if (isHandingOffToProductSheet) {
        dispatchProductSheetDrag('move', currentTouchY);
        previousTouchY = currentTouchY;
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const dragDistance = startY - currentTouchY;
      const movementSincePreviousEvent = previousTouchY - currentTouchY;
      if (Math.abs(dragDistance) < 4) return;

      const maximumScrollTop = Math.max(0, slider.scrollHeight - slider.clientHeight);
      const remainingScrollDistance = Math.max(0, maximumScrollTop - slider.scrollTop);
      const isDraggingTowardPreviousPageContent =
        movementSincePreviousEvent < 0 && slider.scrollTop <= 0;
      const isDraggingTowardFollowingPageContent =
        movementSincePreviousEvent > 0 &&
        remainingScrollDistance <= movementSincePreviousEvent + 1;

      if (isDraggingTowardFollowingPageContent) {
        const movementAvailableForProductSheet = Math.max(
          0,
          movementSincePreviousEvent - remainingScrollDistance
        );
        const productSheetDragStartY = currentTouchY + movementAvailableForProductSheet;

        slider.scrollTop = maximumScrollTop;
        isHandingOffToProductSheet = true;
        dispatchProductSheetDrag('start', productSheetDragStartY);
        dispatchProductSheetDrag('move', currentTouchY);
        previousTouchY = currentTouchY;
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (isDraggingTowardPreviousPageContent) {
        tracking = false;
        return;
      }

      previousTouchY = currentTouchY;
    }, { capture: true, passive: false });

    const finish = () => {
      if (isHandingOffToProductSheet) {
        dispatchProductSheetDrag('end', previousTouchY);
      }

      tracking = false;
      isHandingOffToProductSheet = false;
    };

    slider.addEventListener('touchend', finish, { capture: true, passive: true });
    slider.addEventListener('touchcancel', finish, { capture: true, passive: true });

    slider.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;

      activePointerId = event.pointerId;
      pointerStartY = event.clientY;
      pointerStartScrollTop = slider.scrollTop;
      pointerDidDrag = false;
      slider.classList.add('is-pointer-dragging');
    });

    slider.addEventListener('pointermove', (event) => {
      if (event.pointerId !== activePointerId) return;

      const dragDistance = pointerStartY - event.clientY;
      if (Math.abs(dragDistance) >= 4 && !pointerDidDrag) {
        pointerDidDrag = true;
        slider.setPointerCapture?.(event.pointerId);
      }
      if (!pointerDidDrag) return;

      slider.scrollTop = pointerStartScrollTop + dragDistance;
      event.preventDefault();
    });

    const finishPointerDrag = (event) => {
      if (event.pointerId !== activePointerId) return;

      if (pointerDidDrag) {
        slider.dataset.suppressLightboxUntil = String(Date.now() + 250);
      }

      if (slider.hasPointerCapture?.(event.pointerId)) {
        slider.releasePointerCapture(event.pointerId);
      }
      slider.classList.remove('is-pointer-dragging');
      activePointerId = null;
      pointerDidDrag = false;
    };

    slider.addEventListener('pointerup', finishPointerDrag);
    slider.addEventListener('pointercancel', finishPointerDrag);

    slider.addEventListener('scroll', () => {
      if (paginationFrame !== null) return;

      paginationFrame = window.requestAnimationFrame(() => {
        paginationFrame = null;
        updateMobileGalleryPagination(slider);
      });
    }, { passive: true });
    slider.addEventListener('wheel', (event) => {
      if (slider.scrollHeight <= slider.clientHeight || Math.abs(event.deltaY) < 1) return;

      const maximumScrollTop = Math.max(0, slider.scrollHeight - slider.clientHeight);
      const targetScrollTop = Math.max(
        0,
        Math.min(maximumScrollTop, slider.scrollTop + event.deltaY)
      );
      const isAtBoundary = targetScrollTop === slider.scrollTop;

      if (isAtBoundary) return;

      event.preventDefault();
      event.stopPropagation();
      slider.scrollTop = targetScrollTop;
    }, { passive: false });
  }

  function initializeMobileProductPagination() {
    const productGallerySliders = document.querySelectorAll(
      '.template-product .product-default .productView-nav[data-image-gallery-main]'
    );

    productGallerySliders.forEach((slider) => {
      if (window.jQuery && slider.classList.contains('slick-initialized')) {
        window.jQuery(slider).slick('unslick');
      }

      rememberProductGalleryMarkup(slider);
      preloadProductGalleryImages(slider);
      slider.classList.add(
        'productView-nav--mobileVerticalList',
        'productView-nav--unifiedVerticalList'
      );

      bindMobileVerticalGallery(slider);
      updateMobileGalleryViewportHeight(slider);
      initializeMobileGalleryPagination(slider);

      const selectedColorInput = slider
        .closest('.productView')
        ?.querySelector('input[data-filter]:checked');

      if (selectedColorInput && slider.dataset.mobileInitialColorApplied !== 'true') {
        slider.dataset.mobileInitialColorApplied = 'true';
        window.setTimeout(() => rebuildMobileGalleryForColor(selectedColorInput), 0);
      }
    });
  }

  function initializeMobileProductSheet() {
    const sheet = document.querySelector(
      '.template-product .product-default [data-product-info-sheet]'
    );
    const handle = sheet?.querySelector('[data-product-sheet-handle]');

    if (!sheet || !handle || sheet.dataset.productSheetBound === 'true') return;

    sheet.dataset.productSheetBound = 'true';

    // GSAP owns the mobile sheet gesture when its browser bundles are available.
    // Keeping this path isolated leaves the product gallery implementation untouched.
    if (window.innerWidth <= 767 && window.gsap && window.Draggable) {
      const animationLibrary = window.gsap;
      const animationPlugins = window.ScrollToPlugin ? [window.ScrollToPlugin] : [];
      animationLibrary.registerPlugin(...animationPlugins);
      let sheetIsExpanded = sheet.classList.contains('is-expanded');
      let gestureStartY = 0;
      let gestureStartedExpanded = false;
      let gestureDidMove = false;
      let gestureNaturalTop = 0;
      let gestureStartOffset = 0;
      let currentSheetOffset = 0;
      let sheetDragInstance = null;
      let sheetIsStuck = false;
      let sheetCollapseRequested = false;
      let collapsedPageScrollY = window.scrollY;
      let stuckTouchStartY = null;

      const setSheetExpanded = (expanded) => {
        sheetIsExpanded = expanded;
        sheetIsStuck = expanded;
        sheet.classList.toggle('is-expanded', expanded);
        sheet.classList.toggle('is-product-sheet-stuck', expanded);
        handle.setAttribute('aria-expanded', String(expanded));
        handle.setAttribute(
          'aria-label',
          expanded ? 'Collapse product information' : 'Expand product information'
        );

      };

      const animateSheetToState = (expand) => {
        const targetSheetOffset = expand ? -gestureNaturalTop : gestureNaturalTop;
        const animationDistance = Math.abs(targetSheetOffset - currentSheetOffset);
        const animationDuration = Math.max(0.24, Math.min(0.8, animationDistance / 900));

        sheet.classList.add('is-settling');
        sheetDragInstance?.disable();

        if (!expand) {
          const pageScrollState = { y: window.scrollY };
          const collapseTargetScroll = Math.max(0, collapsedPageScrollY);
          const collapseDistance = Math.abs(pageScrollState.y - collapseTargetScroll);
          const collapseDuration = Math.max(0.3, Math.min(0.8, collapseDistance / 900));

          // Keep the sheet sticky and move the document back to its original
          // position. Sticky positioning then releases progressively, revealing
          // the gallery at exactly the reverse rate of the covering motion.
          animationLibrary.set(sheet, { y: 0 });
          currentSheetOffset = 0;
          animationLibrary.to(pageScrollState, {
            duration: collapseDuration,
            y: collapseTargetScroll,
            ease: 'none',
            onUpdate: () => window.scrollTo(0, pageScrollState.y),
            onComplete: () => {
              window.scrollTo(0, collapseTargetScroll);
              sheet.classList.remove('is-settling', 'is-dragging');
              setSheetExpanded(false);
              sheetCollapseRequested = false;
              sheetDragInstance?.enable();
            }
          });
          return;
        }

        animationLibrary.to(sheet, {
          duration: animationDuration,
          y: targetSheetOffset,
          ease: 'none',
          onComplete: () => {
            sheet.classList.remove('is-settling', 'is-dragging');
            setSheetExpanded(true);
            // Move the document to the sheet anchor before handing scrolling
            // back to the browser's native sticky behavior.
            const targetPageScroll = Math.max(0, window.scrollY + gestureNaturalTop);
            window.scrollTo(0, targetPageScroll);
            animationLibrary.set(sheet, { y: 0 });
            currentSheetOffset = 0;
            // While stuck, upward swipes belong to the page. A dedicated
            // directional touch handler below owns only the downward restore.
            sheetDragInstance?.disable();
          }
        });
      };

      const beginSheetGesture = (clientY) => {
        animationLibrary.killTweensOf(sheet);
        gestureStartY = clientY;
        gestureStartedExpanded = sheetIsExpanded;
        gestureDidMove = false;
        gestureStartOffset = currentSheetOffset;
        sheetCollapseRequested = false;
        if (!sheetIsExpanded) collapsedPageScrollY = window.scrollY;
        gestureNaturalTop = sheetIsExpanded
          ? Math.max(0, sheet.dataset.productSheetCollapsedTop || 0)
          : Math.max(0, sheet.getBoundingClientRect().top);
        sheet.classList.remove('is-settling');
      };

      const moveSheetGesture = (clientY) => {
        const verticalDistance = clientY - gestureStartY;

        // Once the sheet is stuck, a downward drag should restore it immediately
        // instead of waiting for a release threshold or native page scrolling.
        if (gestureStartedExpanded && verticalDistance > 8 && !sheetCollapseRequested) {
          gestureDidMove = true;
          sheetCollapseRequested = true;
          animateSheetToState(false);
          return;
        }

        const minimumSheetOffset = -gestureNaturalTop;
        const maximumSheetOffset = gestureNaturalTop;
        const nextSheetOffset = Math.max(
          minimumSheetOffset,
          Math.min(maximumSheetOffset, gestureStartOffset + verticalDistance)
        );

        gestureDidMove = Math.abs(verticalDistance) >= 8;
        if (!gestureDidMove) return;

        sheet.classList.add('is-dragging');
        animationLibrary.set(sheet, { y: nextSheetOffset });
        currentSheetOffset = nextSheetOffset;
      };

      const endSheetGesture = () => {
        if (!gestureDidMove) {
          // A tap on the expanded sheet must not reset its translated position.
          if (!gestureStartedExpanded) {
            animationLibrary.set(sheet, { y: 0 });
            currentSheetOffset = 0;
          }
          return;
        }

        const threshold = Math.min(48, window.innerHeight * 0.08);
        const shouldExpand = !gestureStartedExpanded && currentSheetOffset < -threshold;
        const shouldCollapse = gestureStartedExpanded && currentSheetOffset > threshold;

        animateSheetToState(
          shouldExpand || (gestureStartedExpanded && !shouldCollapse)
        );
      };

      sheetDragInstance = window.Draggable.create(sheet, {
        type: 'y',
        trigger: sheet,
        minimumMovement: 8,
        // The sheet is no longer an inner scroll container after it sticks.
        // Let Draggable receive the downward gesture so it can restore it.
        allowNativeTouchScrolling: false,
        onPress() {
          beginSheetGesture(this.pointerY);
        },
        onDrag() {
          moveSheetGesture(this.pointerY);
        },
        onRelease() {
          if (sheetCollapseRequested) {
            this.endDrag();
            return;
          }

          if (!gestureDidMove) {
            this.endDrag();
            return;
          }

          endSheetGesture();
        }
      })[0];

      sheet.addEventListener('touchstart', (event) => {
        if (!sheetIsExpanded || event.touches.length !== 1) return;

        stuckTouchStartY = event.touches[0].clientY;
        sheetCollapseRequested = false;
      }, { passive: true });

      sheet.addEventListener('touchmove', (event) => {
        if (
          !sheetIsExpanded ||
          stuckTouchStartY === null ||
          sheetCollapseRequested ||
          event.touches.length !== 1
        ) return;

        const verticalDistance = event.touches[0].clientY - stuckTouchStartY;

        // Upward movement remains native page scrolling. Only a deliberate
        // downward pull restores the sheet and reveals the gallery.
        if (verticalDistance <= 8) return;

        event.preventDefault();
        event.stopPropagation();
        gestureStartedExpanded = true;
        gestureNaturalTop = Math.max(0, Number(sheet.dataset.productSheetCollapsedTop) || 0);
        sheetCollapseRequested = true;
        animateSheetToState(false);
      }, { passive: false });

      const resetStuckTouch = () => {
        stuckTouchStartY = null;
      };

      sheet.addEventListener('touchend', resetStuckTouch, { passive: true });
      sheet.addEventListener('touchcancel', resetStuckTouch, { passive: true });

      sheet.dataset.productSheetCollapsedTop = String(
        Math.max(0, Math.round(sheet.getBoundingClientRect().top))
      );
      document.addEventListener('product:sheet-drag-start', (event) => {
        if (sheetIsExpanded || sheetIsStuck) return;
        beginSheetGesture(event.detail?.clientY || 0);
      });
      document.addEventListener('product:sheet-drag-move', (event) => {
        if (sheetIsExpanded || sheetIsStuck) return;
        moveSheetGesture(event.detail?.clientY || gestureStartY);
      });
      document.addEventListener('product:sheet-drag-end', endSheetGesture);

      sheet.dataset.productSheetAnimationLibrary = 'gsap';
      return;
    }

    const productTop = sheet.closest('.productView-top');
    let activeGesture = null;
    let activeMousePointerId = null;
    let pendingSheetOffset = null;
    let sheetAnimationFrame = null;
    let settlingTimer = null;
    let suppressNextHandleClick = false;
    let sheetAnchorTop = 0;
    let isTouchingSheetPage = false;
    let isBoundaryCorrecting = false;
    let boundaryCorrectionTimer = null;
    let scrollStopTimer = null;
    let previousPageScrollY = window.scrollY;
    let boundaryClampedForGesture = false;

    const isExpanded = () => sheet.classList.contains('is-expanded');

    const getLayoutDocumentTop = (element) => {
      let documentTop = 0;
      let currentElement = element;

      while (currentElement) {
        documentTop += currentElement.offsetTop;
        currentElement = currentElement.offsetParent;
      }

      return documentTop;
    };

    const setExpandedState = (expanded) => {
      sheet.classList.toggle('is-expanded', expanded);
      if (!expanded) {
        productTop?.classList.remove('is-product-gallery-hidden');
      }
      if (expanded) {
        sheetAnchorTop = getLayoutDocumentTop(sheet);
        previousPageScrollY = window.scrollY;
        boundaryClampedForGesture = false;
      } else {
        isBoundaryCorrecting = false;
        window.clearTimeout(boundaryCorrectionTimer);
        window.clearTimeout(scrollStopTimer);
      }
      handle.setAttribute('aria-expanded', String(expanded));
      handle.setAttribute(
        'aria-label',
        expanded ? 'Collapse product information' : 'Expand product information'
      );
    };

    const hideGalleryAfterExpandedScroll = (currentPageScrollY) => {
      if (
        window.innerWidth > 767 ||
        !isExpanded() ||
        settlingTimer !== null ||
        !productTop ||
        productTop.classList.contains('is-product-gallery-hidden') ||
        currentPageScrollY <= sheetAnchorTop + 4 ||
        currentPageScrollY <= previousPageScrollY
      ) return false;

      const previousAnchorTop = getLayoutDocumentTop(sheet);
      productTop.classList.add('is-product-gallery-hidden');
      const updatedAnchorTop = getLayoutDocumentTop(sheet);
      const removedGalleryHeight = Math.max(0, previousAnchorTop - updatedAnchorTop);

      sheetAnchorTop = updatedAnchorTop;
      isBoundaryCorrecting = true;
      window.scrollTo(0, Math.max(sheetAnchorTop, currentPageScrollY - removedGalleryHeight));

      window.clearTimeout(boundaryCorrectionTimer);
      boundaryCorrectionTimer = window.setTimeout(() => {
        isBoundaryCorrecting = false;
        previousPageScrollY = window.scrollY;
      }, 32);
      return true;
    };

    const applyPendingSheetOffset = () => {
      sheetAnimationFrame = null;
      if (pendingSheetOffset !== null) {
        sheet.style.setProperty('--product-sheet-drag-y', `${pendingSheetOffset}px`);
        pendingSheetOffset = null;
      }
    };

    const scheduleSheetOffsetUpdate = () => {
      if (sheetAnimationFrame !== null) return;

      sheetAnimationFrame = window.requestAnimationFrame(applyPendingSheetOffset);
    };

    const flushPendingSheetOffset = () => {
      if (sheetAnimationFrame !== null) {
        window.cancelAnimationFrame(sheetAnimationFrame);
      }

      applyPendingSheetOffset();
    };

    const settleSheet = (expand) => {
      flushPendingSheetOffset();

      if (settlingTimer !== null) {
        window.clearTimeout(settlingTimer);
      }

      sheet.classList.add('is-settling');
      setExpandedState(expand);

      const targetScrollTop = expand
        ? getLayoutDocumentTop(sheet)
        : getLayoutDocumentTop(productTop);

      if (expand) {
        window.scrollTo({
          top: Math.max(0, targetScrollTop),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth'
        });
        sheet.style.setProperty('--product-sheet-drag-y', '0px');
      } else {
        sheet.style.setProperty('--product-sheet-drag-y', '0px');
        window.scrollTo({
          top: Math.max(0, targetScrollTop),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth'
        });
      }

      settlingTimer = window.setTimeout(() => {
        sheet.classList.remove('is-settling', 'is-dragging');
        settlingTimer = null;
      }, 420);
    };

    const correctExpandedBoundary = () => {
      if (
        window.innerWidth > 767 ||
        !isExpanded() ||
        settlingTimer !== null ||
        isTouchingSheetPage ||
        activeGesture ||
        isBoundaryCorrecting
      ) return;

      sheetAnchorTop = getLayoutDocumentTop(sheet);
      const boundaryDistance = sheetAnchorTop - window.scrollY;
      if (boundaryDistance <= 1) return;

      isBoundaryCorrecting = true;
      boundaryClampedForGesture = true;
      window.scrollTo(0, sheetAnchorTop);

      window.clearTimeout(boundaryCorrectionTimer);
      boundaryCorrectionTimer = window.setTimeout(() => {
        isBoundaryCorrecting = false;
        previousPageScrollY = window.scrollY;
      }, 32);
    };

    const scheduleBoundaryCorrection = () => {
      if (!isExpanded() || settlingTimer !== null || isTouchingSheetPage || isBoundaryCorrecting) return;

      window.clearTimeout(scrollStopTimer);
      scrollStopTimer = window.setTimeout(correctExpandedBoundary, 140);
    };

    const resetGesture = () => {
      activeGesture = null;
      activeMousePointerId = null;
      pendingSheetOffset = null;

      if (sheetAnimationFrame !== null) {
        window.cancelAnimationFrame(sheetAnimationFrame);
        sheetAnimationFrame = null;
      }
    };

    const finishGesture = () => {
      if (!activeGesture) return;

      flushPendingSheetOffset();

      if (activeGesture.didDrag) {
        suppressNextHandleClick = true;
      }

      if (!activeGesture.didDrag) {
        sheet.classList.remove('is-dragging');
        sheet.style.setProperty('--product-sheet-drag-y', '0px');
        resetGesture();
        return;
      }

      const threshold = Math.min(120, window.innerHeight * 0.16);
      const shouldExpand = !activeGesture.startedExpanded &&
        (activeGesture.offset < -threshold || activeGesture.velocityY < -0.55);
      const shouldCollapse = activeGesture.startedExpanded &&
        (activeGesture.offset > threshold || activeGesture.velocityY > 0.55);

      settleSheet(shouldExpand || (activeGesture.startedExpanded && !shouldCollapse));
      resetGesture();
    };

    const startGesture = (clientY, source) => {
      if (activeGesture) {
        sheet.classList.remove('is-dragging');
        sheet.style.setProperty('--product-sheet-drag-y', '0px');
        resetGesture();
      }

      if (settlingTimer !== null) {
        window.clearTimeout(settlingTimer);
        settlingTimer = null;
      }

      const startedExpanded = isExpanded();
      const canCollapseExpandedSheet = startedExpanded &&
        window.scrollY <= sheetAnchorTop + 2 &&
        !isBoundaryCorrecting;

      if (startedExpanded && source === 'gallery') return false;
      if (startedExpanded && !canCollapseExpandedSheet) return false;

      activeGesture = {
        source,
        startY: clientY,
        previousY: clientY,
        previousTime: performance.now(),
        offset: 0,
        velocityY: 0,
        startedExpanded,
        didDrag: false
      };
      pendingSheetOffset = null;
      sheet.classList.remove('is-settling');
      return true;
    };

    const moveGesture = (clientY) => {
      if (!activeGesture) return false;

      const now = performance.now();
      const deltaY = clientY - activeGesture.startY;
      const directionIsAllowed = activeGesture.startedExpanded
        ? deltaY > 0
        : deltaY < 0;

      if (!directionIsAllowed || Math.abs(deltaY) < 8) return false;

      if (!activeGesture.didDrag) {
        activeGesture.didDrag = true;
        sheet.classList.add('is-dragging');
      }

      const rawOffset = activeGesture.startedExpanded
        ? Math.max(0, deltaY)
        : Math.min(0, deltaY);
      const maximumOffset = window.innerHeight;
      const currentVelocity = (clientY - activeGesture.previousY) /
        Math.max(1, now - activeGesture.previousTime);

      activeGesture.offset = Math.max(
        -maximumOffset,
        Math.min(maximumOffset, rawOffset * 0.85)
      );
      activeGesture.velocityY = activeGesture.velocityY * 0.7 + currentVelocity * 0.3;
      activeGesture.previousY = clientY;
      activeGesture.previousTime = now;
      pendingSheetOffset = activeGesture.offset;
      scheduleSheetOffsetUpdate();
      return true;
    };

    sheet.addEventListener('pointerdown', (event) => {
      if (window.innerWidth > 767 || event.button !== 0 || event.pointerType === 'touch') return;

      if (startGesture(event.clientY, 'sheet')) {
        activeMousePointerId = event.pointerId;
      }
    });

    sheet.addEventListener('pointermove', (event) => {
      if (event.pointerId !== activeMousePointerId) return;

      if (moveGesture(event.clientY)) event.preventDefault();
    });

    sheet.addEventListener('pointerup', finishGesture);
    sheet.addEventListener('pointercancel', finishGesture);
    sheet.addEventListener('touchstart', (event) => {
      if (window.innerWidth > 767 || event.touches.length !== 1) return;

      startGesture(event.touches[0].clientY, 'sheet');
    }, { passive: true });
    sheet.addEventListener('touchmove', (event) => {
      if (!activeGesture || activeGesture.source !== 'sheet' || event.touches.length !== 1) return;

      if (moveGesture(event.touches[0].clientY)) event.preventDefault();
    }, { passive: false });
    sheet.addEventListener('touchend', finishGesture, { passive: true });
    sheet.addEventListener('touchcancel', finishGesture, { passive: true });
    document.addEventListener('touchstart', (event) => {
      if (window.innerWidth > 767 || !isExpanded() || event.touches.length !== 1) return;

      isTouchingSheetPage = true;
      boundaryClampedForGesture = false;
      previousPageScrollY = window.scrollY;
      window.clearTimeout(scrollStopTimer);
    }, { capture: true, passive: true });
    window.addEventListener('scroll', () => {
      const currentPageScrollY = window.scrollY;

      if (hideGalleryAfterExpandedScroll(currentPageScrollY)) return;

      const crossedExpandedBoundary = isExpanded() &&
        !isTouchingSheetPage &&
        !activeGesture &&
        !isBoundaryCorrecting &&
        !boundaryClampedForGesture &&
        previousPageScrollY >= sheetAnchorTop - 1 &&
        currentPageScrollY < sheetAnchorTop - 1;

      if (crossedExpandedBoundary) {
        window.clearTimeout(scrollStopTimer);
        isBoundaryCorrecting = true;
        boundaryClampedForGesture = true;
        window.scrollTo(0, sheetAnchorTop);
        window.clearTimeout(boundaryCorrectionTimer);
        boundaryCorrectionTimer = window.setTimeout(() => {
          isBoundaryCorrecting = false;
          previousPageScrollY = window.scrollY;
        }, 32);
        return;
      }

      previousPageScrollY = currentPageScrollY;
      scheduleBoundaryCorrection();
    }, { passive: true });
    window.addEventListener('scrollend', correctExpandedBoundary, { passive: true });
    const finishPageTouch = () => {
      if (!isTouchingSheetPage) return;

      isTouchingSheetPage = false;
      scheduleBoundaryCorrection();
    };
    document.addEventListener('touchend', finishPageTouch, { capture: true, passive: true });
    document.addEventListener('touchcancel', finishPageTouch, {
      capture: true,
      passive: true
    });
    document.addEventListener('product:sheet-drag-start', (event) => {
      if (window.innerWidth > 767 || activeGesture) return;

      startGesture(event.detail.clientY, 'gallery');
    });
    document.addEventListener('product:sheet-drag-move', (event) => {
      if (!activeGesture || activeGesture.source !== 'gallery') return;

      moveGesture(event.detail.clientY);
    });
    document.addEventListener('product:sheet-drag-end', () => {
      if (!activeGesture || activeGesture.source !== 'gallery') return;

      finishGesture();
    });
    handle.addEventListener('click', () => {
      if (window.innerWidth > 767) return;

      if (suppressNextHandleClick) {
        suppressNextHandleClick = false;
        return;
      }

      settleSheet(!isExpanded());
    });

    window.addEventListener('resize', () => {
      if (window.innerWidth > 767) {
        productTop?.classList.remove('is-product-gallery-hidden');
      }

      if (window.innerWidth <= 767 && isExpanded()) {
        sheetAnchorTop = getLayoutDocumentTop(sheet);
      }
    });

  }

  function updateMobileProductViewport() {
    const productTop = document.querySelector(
      '.template-product .product-default .productView-top'
    );

    if (!productTop) return;

    if (window.innerWidth > 767) {
      productTop.style.removeProperty('--product-mobile-top-offset');
      return;
    }

    const documentTop = productTop.getBoundingClientRect().top + window.scrollY;
    productTop.style.setProperty(
      '--product-mobile-top-offset',
      `${Math.max(0, Math.round(documentTop))}px`
    );
  }

  function waitForMobileProductGallery(attempt = 0) {
    initializeMobileProductPagination();

    const gallery = document.querySelector(
      '.template-product .product-default .productView-nav[data-image-gallery-main]'
    );
    const pagination = gallery
      ?.closest('.productView-image-wrapper')
      ?.querySelector('[data-mobile-product-pagination]');

    if (
      gallery &&
      (
        gallery.classList.contains('slick-initialized') ||
        !gallery.classList.contains('productView-nav--unifiedVerticalList') ||
        !pagination
      ) &&
      attempt < 40
    ) {
      window.setTimeout(() => waitForMobileProductGallery(attempt + 1), 100);
    }
  }

  function initialize() {
    document
      .querySelectorAll('.template-product .product-default .productView.layout-1 .productView-for')
      .forEach(bindThumbnailWheel);

    document
      .querySelectorAll('.template-product .product-default .tabs-contents .toggle-content.is-active')
      .forEach((content) => content.classList.remove('is-active'));

    document
      .querySelectorAll('.template-product .product-default .tabs-contents .toggleLink.is-open')
      .forEach((link) => link.classList.remove('is-open'));

    arrangeProductDetails();
    updateMobileProductViewport();
    waitForMobileProductGallery();
    initializeMobileProductSheet();
  }

  document.addEventListener('DOMContentLoaded', initialize);
  window.addEventListener('load', () => {
    initialize();

    const selectedColorInput = document.querySelector(
      '.template-product .product-default input[data-filter]:checked'
    );

    if (selectedColorInput) {
      window.setTimeout(() => {
        rebuildMobileGalleryForColor(selectedColorInput);
      }, 100);
    }
  });
  window.addEventListener('resize', () => {
    updateMobileProductViewport();
    waitForMobileProductGallery();
  });

  document.addEventListener('product:mobile-gallery-updated', () => {
    updateMobileProductViewport();
    waitForMobileProductGallery();
  });

  document.addEventListener('change', (event) => {
    const colorInput = event.target.closest('input[data-filter]');
    if (!colorInput) return;

    window.setTimeout(() => {
      rebuildMobileGalleryForColor(colorInput);
    }, 0);
  }, true);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-product-fancybox="images"]');
    if (!trigger || !window.jQuery?.fancybox) return;

    const gallery = trigger.closest('[data-image-gallery-main]');
    const suppressLightboxUntil = Number(gallery?.dataset.suppressLightboxUntil || 0);

    if (Date.now() < suppressLightboxUntil) {
      event.preventDefault();
      return;
    }

    event.preventDefault();

    const triggers = getVisibleProductLightboxTriggers(trigger);
    const items = triggers.map((item) => {
      const image = item.querySelector('img');

      return {
        src: item.getAttribute('href'),
        type: 'image',
        opts: {
          thumb: image?.currentSrc || image?.src || ''
        }
      };
    });

    const startIndex = Math.max(0, triggers.indexOf(trigger));
    const instance = window.jQuery.fancybox.open(items, lightboxOptions, startIndex);

    renderLightboxThumbs(instance, startIndex);
  });
})();
